//-- Copyright (c) 2021 TMT Insights Group, LLC
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from '@/_utils/axios';
import { LoadingStatuses, TitleTypes, DEFAULT_COMMON_CONSTANTS } from '@/constants/constants';
import { alphabetizeByKey, convertToKeyVal } from '@/_utils/utils';
import { AxiosResponse } from 'axios';
import {
  Constants,
  ConstantsResponse,
  ConstantsState,
  Customer,
  CustomerResponse,
  FocusConstants,
  FocusConstantsResponse,
  LanguageResponse
} from '@/redux/slices/slice_types';
import { ClientConfig } from '@/__configs/config_types';
import { availLicenses, regionFilterValues } from '@/components/Focus/constants';

// THUNKS
export const getConstants = createAsyncThunk('constants/getConstants', async (payload: ClientConfig | undefined) => {
  const commonConstants = payload?.commonConstants;
  return axios
    .get(`/common/constants/`)
    .then((res: AxiosResponse<ConstantsResponse>): Constants => ({ ...res.data.data, commonConstants }))
    .catch((err) => {
      console.error(err);
    });
});

export const getFocusConstants = createAsyncThunk('constants/getFocusConstants', async () => {
  return axios
    .get(`/focus/common/constants/`)
    .then((res: AxiosResponse<FocusConstantsResponse>): FocusConstants => res.data.data)
    .catch((err) => {
      console.error(err);
    });
});

export const getLanguages = createAsyncThunk('constants/getLanguages', async () => {
  return axios
    .get(`/common/languages/`)
    .then((res: AxiosResponse<LanguageResponse>): LanguageResponse => res.data)
    .catch((err) => {
      console.error(err);
    });
});

export const getCustomers = createAsyncThunk('constants/getCustomers', async () => {
  // TODO: This should probably be paginated at 99 as should all the rest of the customer requests
  // so request list, if total > page_size, request additional pages until all customers are retrieved
  return axios.post(`/core/customers/search/?page_size=999`).then((res: AxiosResponse<CustomerResponse>): CustomerResponse => res.data);
});

export const getProfileCustomers = createAsyncThunk('constants/getProfileCustomers', async () => {
  return axios
    .post(`/core/customers/search/?page_size=999`, { has_profile_filter: true })
    .then((res: AxiosResponse<CustomerResponse>): CustomerResponse => res.data);
});

export const getBillableCustomers = createAsyncThunk('constants/getBillableCustomers', async () => {
  return axios
    .post(`/core/customers/search/?page_size=999`, { is_billable_customer: true })
    .then((res: AxiosResponse<CustomerResponse>): CustomerResponse => res.data);
});

export const getNonBillableCustomers = createAsyncThunk('constants/getNonBillableCustomers', async () => {
  return axios
    .post(`/core/customers/search/?page_size=999`, { is_billable_customer: false })
    .then((res: AxiosResponse<CustomerResponse>): CustomerResponse => res.data);
});

function setConstants(state: ConstantsState, values: Constants) {
  if (!values) return;
  const {
    accurate_key,
    order_status,
    task_item_status,
    job_status,
    job_stages,
    job_types,
    file_ingest_status,
    ingest_manager_status,
    ingest_manager_stage,
    ingest_manager_file_types,
    file_type_groupings,
    job_workflows,
    task_status,
    task_names,
    manual_task_names,
    task_type_groups,
    network_codes,
    assignable_task_groups,
    job_asset_status,
    job_asset_system_status,
    job_asset_human_status,
    job_asset_component_types,
    order_job_asset_types,
    grunt_task_status,
    order_categories,
    media_resolutions,
    commonConstants,
    entity_type_labels,
    task_qc_screening_results,
    title_types,
    bulk_actions,
    vfs_task_types,
    monitoring_event_priority,
    monitoring_event_reference_type,
    monitoring_event_status,
    monitoring_event_subtype,
    monitoring_event_type,
    monitoring_event_severity,
    cut_type_labels,
    avail_territory_groupings,
    avail_geo_territory_groupings,
    avail_territory_timezone_groupings,
    job_priority_levels,
    qc_task_types,
    hierarchies,
    focus_avails_availed_platform,
    entity_status,
    ...rest
  } = values;

  state.orders.statuses = convertToKeyVal(order_status);
  if (task_item_status) state.orders.taskItemStatuses = convertToKeyVal(task_item_status);
  if (bulk_actions) state.bulkActions = bulk_actions;
  state.jobs.statuses = convertToKeyVal(job_status);
  state.jobs.stages = convertToKeyVal(job_stages);
  state.jobs.types = convertToKeyVal(job_types);
  if (sessionStorage.getItem('ingestVersion') === 'v1') {
    state.fileIngest.statuses = convertToKeyVal(file_ingest_status);
  } else {
    state.fileIngest.statuses = convertToKeyVal(ingest_manager_status);
  }
  state.titles.entity_status = entity_status ? convertToKeyVal(entity_status) : undefined;
  state.titles.hierarchies = hierarchies ? alphabetizeByKey(convertToKeyVal(hierarchies.map((l) => [l, l]))) : {};
  state.fileIngest.stages = convertToKeyVal(ingest_manager_stage);
  state.fileIngest.file_types = convertToKeyVal(ingest_manager_file_types);
  state.fileTypeGroupingsFromApi = file_type_groupings;
  state.jobs.jobWorkflows = alphabetizeByKey(convertToKeyVal(job_workflows));
  state.jobs.job_priority_levels = job_priority_levels ? alphabetizeByKey(convertToKeyVal(job_priority_levels.map((l) => [l, l]))) : {};
  state.tasks.qc_task_types = qc_task_types ? alphabetizeByKey(convertToKeyVal(qc_task_types.map((q) => [q, q]))) : {};
  state.tasks.statuses = convertToKeyVal(task_status);
  state.tasks.types = convertToKeyVal(task_names);
  state.tasks.manualTaskNames = convertToKeyVal(manual_task_names);
  state.tasks.taskTypeGroups = convertToKeyVal(task_type_groups);
  state.tasks.networkCodes = network_codes;
  if (task_qc_screening_results) {
    state.tasks.task_qc_screening_results = convertToKeyVal(task_qc_screening_results || []);
  }
  state.pendingAssets.assignable_task_groups = convertAssignableGroupTasks(assignable_task_groups as [number, string][]);
  state.pendingAssets.statuses = convertToKeyVal(job_asset_status);
  state.pendingAssets.systemStatuses = convertToKeyVal(job_asset_system_status);
  state.pendingAssets.humanStatuses = convertToKeyVal(job_asset_human_status);
  state.pendingAssets.componentTypes = convertToKeyVal(job_asset_component_types);
  state.pendingAssets.contentTypes = convertToKeyVal(order_job_asset_types);
  state.transferQueue.statuses = convertToKeyVal(grunt_task_status);
  state.orders.categories = convertToKeyVal(order_categories);
  state.vfsTaskTypes = convertToKeyVal(vfs_task_types);
  state.media_resolutions = media_resolutions;
  if (!state.packaging) {
    state.packaging = { cutTypeLabels: {} };
  }
  state.packaging.cutTypeLabels = cut_type_labels ? convertToKeyVal(cut_type_labels) : {};
  state.packaging.accurate_key = accurate_key;
  state.commonConstants = {
    ...DEFAULT_COMMON_CONSTANTS,
    ...(commonConstants || {})
  };

  if (!!entity_type_labels || !!title_types) {
    state.titles.types = { ...TitleTypes, ...convertToKeyVal((entity_type_labels || title_types) as any[][]) };
  }

  if (entity_type_labels) {
    state.titles.entityTypeLabels = convertToKeyVal(entity_type_labels);
  }

  state.exceptions.priorities = convertToKeyVal(monitoring_event_priority);
  state.exceptions.referenceTypes = convertToKeyVal(monitoring_event_reference_type);
  state.exceptions.statuses = convertToKeyVal(monitoring_event_status);
  state.exceptions.subtypes = convertToKeyVal(monitoring_event_subtype);
  state.exceptions.types = convertToKeyVal(monitoring_event_type);
  state.exceptions.severities = convertToKeyVal(monitoring_event_severity);

  state.api_extras = rest;

  //Temp stuff for focus avails

  state.avails.localeTerritory = convertToKeyVal(regionFilterValues);
  state.avails.license = convertToKeyVal(availLicenses);
  state.avails.availedPlatform = convertToKeyVal(focus_avails_availed_platform);
  state.avails.scheduleStatus = {
    draft: 'Draft',
    confirmed: 'Submitted'
  };

  // Territory Groupings
  state.territoryGroupings = avail_territory_groupings;
  state.geoTerritoryGroupings = avail_geo_territory_groupings;
  state.timezoneTerritoryGroupings = avail_territory_timezone_groupings;
  state.initFinished = true;
}

const setFocusConstants = (state: ConstantsState, values: FocusConstants) => {
  if (!values) return;
  state.avails.license = convertToKeyVal(values.focus_avails_license);
  state.avails.localeTerritory = convertToKeyVal(values.focus_avails_territory);
  state.avails.availedPlatform = convertToKeyVal(values.focus_avails_availed_platform);
  state.avails.platformIds = values.focus_avails_availed_platform.reduce((memo, item) => ({ ...memo, [item[0]]: item[2] }), {});
};

const initialState: ConstantsState = {
  orders: {
    statuses: {},
    taskItemStatuses: {},
    customerIDNames: {}
  },
  jobs: {
    statuses: {},
    stages: {},
    types: {},
    jobWorkflows: {}
  },
  tasks: {
    statuses: {},
    types: {},
    manualTaskNames: {},
    taskTypeGroups: {},
    networkCodes: {}
  },
  pendingAssets: {
    statuses: {},
    systemStatuses: {},
    humanStatuses: {},
    assignable_task_groups: []
  },
  titles: {
    types: TitleTypes
  },
  fileIngest: {
    statuses: {},
    stages: {},
    file_types: {}
  },
  transferQueue: {},
  userEmails: {},
  vfs_playback_base_url: '',
  vfsTaskTypes: {},
  initFinished: false,
  status: LoadingStatuses.pending,
  media_resolutions: [],
  exceptions: {
    priorities: {},
    referenceTypes: {},
    statuses: {},
    subtypes: {},
    types: {},
    severities: {}
  },
  avails: {
    localeTerritory: {},
    localeDialect: {},
    license: {},
    availedPlatform: {},
    availedLanguage: {},
    scheduleStatus: {},
    platformIds: {}
  },
  packaging: {
    cutTypeLabels: {}
  },
  territoryGroupings: {}
};

export const sliceConfig = {
  name: 'constants',
  initialState: initialState,
  reducers: {
    addUserEmailOptions(state: ConstantsState, { payload }) {
      state.userEmails = Object.assign(state.userEmails, payload);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getConstants.pending, (state: ConstantsState) => {
      state.orders.status = LoadingStatuses.pending;
    });
    builder.addCase(getConstants.fulfilled, (state: ConstantsState, { payload }: PayloadAction<void | Constants>) => {
      setConstants(state, payload as Constants);
      state.orders.status = LoadingStatuses.fulfilled;
    });
    builder.addCase(getConstants.rejected, (state: ConstantsState) => {
      state.orders.status = LoadingStatuses.rejected;
    });

    builder.addCase(getFocusConstants.fulfilled, (state: ConstantsState, { payload }: PayloadAction<void | FocusConstants>) => {
      setFocusConstants(state, payload as FocusConstants);
    });

    builder.addCase(getLanguages.fulfilled, (state: ConstantsState, { payload }: PayloadAction<void | LanguageResponse>) => {
      if (payload?.data) state.languages = payload.data;
    });

    builder.addCase(getCustomers.fulfilled, (state: ConstantsState, { payload: { data } }: PayloadAction<CustomerResponse>) => {
      if (!data) return;
      data.forEach((customer) => {
        state.orders.customerIDNames[customer.id] = customer.name;
      });
      data?.sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
      state.orders.customers = data?.reduce((memo: any, customer: Customer) => {
        if (customer.name.length) {
          return {
            ...memo,
            [customer.name]: customer.name
          };
        }
      }, {});
    });
    builder.addCase(getProfileCustomers.fulfilled, (state: ConstantsState, { payload: { data } }: PayloadAction<CustomerResponse>) => {
      if (!data) return;
      data?.sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
      state.orders.profileCustomers = data?.reduce((memo: any, customer: Customer) => {
        if (customer.name.length) {
          return { ...memo, [customer.name]: customer.name };
        }
      }, {});
    });

    builder.addCase(getBillableCustomers.fulfilled, (state: ConstantsState, { payload: { data } }: PayloadAction<CustomerResponse>) => {
      if (!data) return;
      state.orders.billableCustomers = data;
    });

    builder.addCase(getNonBillableCustomers.fulfilled, (state: ConstantsState, { payload: { data } }: PayloadAction<CustomerResponse>) => {
      if (!data) return;
      state.orders.nonBillableCustomers = data;
    });
  }
};

const constantsSlice = createSlice(sliceConfig);

export const { addUserEmailOptions } = constantsSlice.actions;

export default constantsSlice.reducer;

function convertAssignableGroupTasks(arr: [number, string][]) {
  return arr.map((group) => ({
    id: group[0],
    group: group[1]
  }));
}
